import React, { useState, useEffect } from 'react';
import { deezerService } from '../services/deezerService';
import TrackDetail from './TrackDetail';
import './SpotifySearch.css';
import '../components/Feed.css';
import { reviewsService } from '../services/reviewsService';

const SpotifySearch = ({ onNavigate, openPlaylistModal }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPreview, setCurrentPreview] = useState(null);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [songRatings, setSongRatings] = useState({}); // Almacenar ratings de canciones

    // Estados para la barra de navegación
    const [showFloatingActions, setShowFloatingActions] = useState(false);
    const [isNavVisible, setIsNavVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        try {
            const searchResults = await deezerService.searchTracks(query);
            setResults(searchResults.data || []);
        } catch (error) {
            console.error('Error en la búsqueda:', error);
            setResults([]);
        }
        setLoading(false);
    };

    // Obtener rating de una canción usando el track.id
    const fetchSongRating = async (trackId) => {
        try {
            const rating = await reviewsService.obtenerPromedioEstrellas('track', trackId.toString());
            return rating;
        } catch (error) {
            console.error('Error obteniendo rating:', error);
            return { promedio: 0, total: 0 };
        }
    };

    // Cargar ratings cuando cambian los resultados
    const loadAllRatings = async () => {
        const ratings = {};
        for (const track of results) {
            const rating = await fetchSongRating(track.id);
            ratings[track.id] = rating;
        }
        setSongRatings(ratings);
    };

    // Hook para detectar scroll y ocultar/mostrar navegación
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollY = window.scrollY;

                    // Solo ocultar si se ha hecho scroll hacia abajo más de 10px
                    if (currentScrollY > lastScrollY && currentScrollY > 10) {
                        setIsNavVisible(false);
                    } else if (currentScrollY < lastScrollY) {
                        setIsNavVisible(true);
                    }

                    setLastScrollY(currentScrollY);
                    ticking = false;
                });

                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [lastScrollY]);

    useEffect(() => {
        if (results.length > 0) {
            loadAllRatings();
        }
    }, [results]);

    const playPreview = (previewUrl, trackId) => {
        if (currentPreview?.audio) currentPreview.audio.pause();
        const audio = new Audio(previewUrl);
        audio.play();
        setCurrentPreview({ audio, trackId });
        audio.onended = () => setCurrentPreview(null);
    };

    const stopPreview = () => {
        if (currentPreview?.audio) {
            currentPreview.audio.pause();
            setCurrentPreview(null);
        }
    };

    const formatDuration = (duration) => {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Funciones para la barra de navegación
    const handleMouseEnter = () => {
        setShowFloatingActions(true);
    };

    const handleMouseLeave = () => {
        setShowFloatingActions(false);
    };

    const handlePlaylist = () => {
        if (openPlaylistModal) {
            openPlaylistModal(null);
        }
        setShowFloatingActions(false);
    };

    const handleLyrics = () => {
        alert("Lyrics - En desarrollo");
        setShowFloatingActions(false);
    };

    const handleCloseReview = () => {
        setSelectedTrack(null);
        // Recargar ratings después de cerrar el modal
        if (results.length > 0) {
            loadAllRatings();
        }
    };

    return (
        <div className="spotify-search-container">
            <div className="search-box-wrapper">
                <div className="search-header">
                    <p style={{ fontSize: '22px', fontWeight: 'bold' }}>
                        <span style={{ color: '#06adf2', fontSize: '28px', marginRight: '8px' }}>★</span>
                        Descubre, califica y comparte tu música favorita
                    </p>
                </div>

                <form onSubmit={handleSearch} className="search-bar-container">
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Busca tu canción favorita..."
                        className="search-input"
                    />
                    <button type="submit" disabled={loading} className="search-button">
                        {loading ? '⏳ Buscando...' :'Buscar'}
                    </button>
                </form>

                {results.length > 0 && !loading && (
                    <div className="search-results-grid">
                        {results.map((track) => {
                            const song = {
                                id: track.id,
                                title: track.title,
                                artist: track.artist?.name,
                                image: track.album?.cover_medium || track.album?.cover_big || "https://placehold.co/200x200?text=Sin+imagen",
                                previewUrl: track.preview,
                                spotifyUrl: `https://open.spotify.com/search/${encodeURIComponent(`${track.title} ${track.artist?.name}`)}`
                            };

                            return (
                                <div key={track.id} className="search-card-wrapper">
                                    <div className="search-card">
                                        <div className="search-card-image-container">
                                            <img
                                                src={song.image}
                                                alt={song.title}
                                                className="search-card-image"
                                            />
                                            {/* Overlay con botón de play */}
                                            {song.previewUrl && (
                                                <div
                                                    className="play-overlay"
                                                    onClick={() =>
                                                        currentPreview?.trackId === track.id
                                                            ? stopPreview()
                                                            : playPreview(track.preview, track.id)
                                                    }
                                                >
                                                    <div className="play-button">
                                                        {currentPreview?.trackId === track.id ? "⏸" : "▶"}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="search-card-meta">
                                            <div className="search-card-title">
                                                {song.title}
                                                {(() => {
                                                    const rating = songRatings[track.id];
                                                    if (rating && rating.total > 0) {
                                                        return (
                                                            <span className="song-rating">
                                                                ⭐ {rating.promedio.toFixed(1)}
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            <div className="search-card-artist">{song.artist}</div>
                                        </div>

                                        <div className="search-card-actions">
                                            <button
                                                className="action-button btn-rate"
                                                onClick={() => setSelectedTrack(track)}
                                            >
                                                ⭐ Calificar
                                            </button>
                                            <button
                                                className="action-button btn-playlist"
                                                onClick={() => openPlaylistModal && openPlaylistModal(song)}
                                            >
                                                ➕ Playlist
                                            </button>
                                        </div>
                                    </div>

                                    {/* Botón "Escuchar Completa" que aparece en hover */}
                                    {song.spotifyUrl && (
                                        <a
                                            href={song.spotifyUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="search-listen-button"
                                        >
                                            🎵 Escuchar Completa
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {results.length === 0 && query && !loading && (
                    <div className="empty-state">
                        <div>🎵</div>
                        <p>No encontramos nada, intenta con otro término</p>
                    </div>
                )}

                {selectedTrack && (
                    <TrackDetail track={selectedTrack} onClose={handleCloseReview} />
                )}
            </div>

            {/* Barra de navegación flotante */}
            <div className={`floating-nav ${!isNavVisible ? 'hidden' : ''}`}>
                <button
                    className="nav-icon"
                    onClick={() => onNavigate && onNavigate('home')}
                >
                    <span className="icon">🏠</span>
                    <span className="icon-label">Inicio</span>
                </button>

                <button
                    className="nav-icon"
                    onClick={() => onNavigate && onNavigate('search')}
                >
                    <span className="icon">🔍</span>
                    <span className="icon-label">Buscar</span>
                </button>

                <div
                    className="nav-center-container"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {showFloatingActions && (
                        <div className="floating-actions">
                            <button
                                className="floating-action-btn"
                                onClick={handlePlaylist}
                            >
                                <span className="floating-action-icon">🎵</span>
                                <span className="floating-action-label">Playlist</span>
                            </button>
                            <button
                                className="floating-action-btn"
                                onClick={handleLyrics}
                            >
                                <span className="floating-action-icon">🎤</span>
                                <span className="floating-action-label">Lyrics</span>
                            </button>
                        </div>
                    )}

                    <button
                        className={`nav-icon nav-icon-add ${showFloatingActions ? 'rotated' : ''}`}
                    >
                        <span className="icon icon-plus">➕</span>
                    </button>
                </div>

                <button
                    className="nav-icon"
                    onClick={() => alert("Notificaciones próximamente")}
                >
                    <span className="icon">❤️</span>
                    <span className="icon-label">Likes</span>
                </button>

                <button
                    className="nav-icon"
                    onClick={() => onNavigate && onNavigate('profile')}
                >
                    <span className="icon">👤</span>
                    <span className="icon-label">Mi Perfil</span>
                </button>
            </div>
        </div>
    );
};

export default SpotifySearch;
